import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import CreatableSelect from "react-select/creatable";
import Select, { components } from "react-select";
import { find } from "lodash-es";
import FieldLabel from "../FieldLabel/FieldLabel";
import Badge from "../Badge/Badge";
import { MdAdd, MdOutlineManageSearch } from "react-icons/md";
import { ICON_SIZES } from "../../helpers/constants";
import useMobile from "src/hooks/useMobile";
import SelectModal from "../Modals/SelectModal";
import Button from "../Button/Button";
import { useTranslation } from "react-i18next";

export interface ICustomSelectOption {
  value: string;
  label: string;
}

interface ICustomSelect {
  options: ICustomSelectOption[];
  placeholder?: string;
  label?: string;
  id?: string;
  required?: boolean;
  value?: string | any[];
  onChange: (e: any) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  form?: string;
  creatable?: boolean;
  useValueAsLabel?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  isMulti?: boolean;
  hideSelectedOptions?: boolean;
  closeMenuOnSelect?: boolean;
  allowSearchModal?: boolean;
}

const CustomValueContainer = (props: any) => {
  const { isMulti } = props.selectProps;
  const { t } = useTranslation();
  if (!isMulti) return <components.ValueContainer {...props} />;

  const selected = props.getValue();
  const children = React.Children.toArray(props.children);
  const input = children[children.length - 1];

  const classNames = clsx(
    "dark:!bg-cyan-400",
    "!bg-gray-400",
    "!text-white",
    "dark:!text-black"
  );

  let badge = null;
  if (selected.length > 1) {
    badge = (
      <Badge
        text={t("select_component.selected_count", {
          count: selected.length,
        })}
        className={classNames}
      />
    );
  } else if (selected.length === 1) {
    badge = <Badge text={selected[0].label} className={classNames} />;
  }

  return (
    <components.ValueContainer {...props}>
      {selected.length > 0 ? badge : children.slice(0, -1)}
      {input}
    </components.ValueContainer>
  );
};

const CustomMenuList = (props: any) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      const selectedIndex = props.children.findIndex(
        (child: any) => child?.props?.isSelected
      );

      const menuEl = menuRef.current;
      const selectedEl = menuEl?.children[selectedIndex];

      if (selectedIndex >= 0 && selectedEl) {
        selectedEl.scrollIntoView({
          block: "center",
        });
      }
    });
  }, []);

  return <components.MenuList {...props} innerRef={menuRef} />;
};

const sharedStyles = {
  valueContainer: (base: any) => ({
    ...base,
    flexWrap: "nowrap",
    overflowX: "auto",
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999, fontSize: 14 }),
  control: (styles: any) => ({
    ...styles,
    borderRadius: 0,
    ":focus": { outline: "0" },
  }),
};

const sharedClassNames = {
  control: () =>
    clsx(
      "!bg-transparent",
      "!text-blue-500",
      "h-[50px]",
      "!border-0",
      "!shadow-none"
    ),
  container: () => clsx("!bg-transparent", "!text-blue-500", "w-full"),
  option: (props: any) =>
    clsx(
      "!bg-transparent",
      "dark:text-white",
      "!cursor-pointer",
      props.isSelected && "!text-blue-500 dark:!text-cyan-400 font-medium"
    ),
  input: () => clsx("dark:!text-white"),
  menu: () =>
    clsx(
      "dark:!bg-black",
      "max-h-[240px]",
      "overflow-y-auto",
      "scrollbar-thin",
      "touch-auto",
      "pointer-events-auto"
    ),
  singleValue: () => clsx("dark:!text-white"),
  placeholder: () =>
    clsx({
      "!text-gray-500 dark:!text-gray-300 truncate": true,
      "!text-blue-500 dark:!text-purple-400 truncate": true,
    }),
};

const sharedClassName = clsx(
  "border-solid",
  "border-b-2",
  "text-sm",
  "md:text-base",
  "rounded-sm",
  "leading-none",
  "h-[40px] md:h-[46px]",
  "font-light",
  "placeholder:text-sm",
  "placeholder:font-light",
  "bg-transparent",
  "dark:text-white"
);

const CustomSelect: React.FC<ICustomSelect> = ({
  options = [],
  label,
  placeholder,
  id,
  required,
  value = "",
  onChange,
  disabled = false,
  autoFocus = false,
  form = "",
  creatable = false,
  useValueAsLabel = true,
  isClearable = true,
  isSearchable = true,
  isMulti = false,
  hideSelectedOptions = false,
  closeMenuOnSelect = true,
  allowSearchModal = false,
}) => {
  const selectAutoId = useId();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useMobile();
  const [showModalSelect, setShowModalSelect] = useState<boolean>(false);

  const selectedValue = useMemo(() => {
    if (Array.isArray(value)) {
      if (value?.length) {
        return value;
      }
    } else {
      if (value) {
        return {
          value,
          label: useValueAsLabel ? value : find(options, { value })?.label,
        };
      }
    }

    return null;
  }, [value, options, useValueAsLabel]);

  const updateValue = useCallback(
    (theNewValue: any) => onChange(theNewValue),
    [onChange]
  );

  const commonProps = {
    components: {
      IndicatorSeparator: () => null,
      ValueContainer: CustomValueContainer,
      MenuList: CustomMenuList,
    },
    styles: sharedStyles,
    classNames: sharedClassNames,
    className: clsx(sharedClassName, {
      "border-blue-300 dark:border-purple-500/80": required && !value,
      "border-gray-200 dark:border-gray-600": (required && value) || !required,
      "border-gray-300": !required,
      "placeholder:text-gray-500": !required,
      "placeholder:text-blue-500": required,
    }),
    menuPortalTarget: document.body,
    menuShouldBlockScroll: isMobile ? false : true,
    required,
    isSearchable,
    hideSelectedOptions,
    closeMenuOnSelect,
    isClearable,
    menuPlacement: "auto" as const,
    options,
    placeholder,
    onChange: updateValue,
    value: selectedValue,
    id: id || selectAutoId,
    isDisabled: disabled,
    isMulti,
    autoFocus,
    openMenuOnClick: true,
    ...(form ? { form } : {}),
  };

  return (
    <>
      <div className="flex flex-col w-full" ref={containerRef}>
        {label && (
          <FieldLabel
            required={required}
            text={label}
            htmlFor={id || selectAutoId}
            valid={!!value}
          />
        )}
        <div className={clsx(["flex", "w-full", "flex-row", "gap-2"])}>
          <div className={clsx(["flex", "w-full", "items-end"])}>
            {creatable ? (
              <CreatableSelect
                {...commonProps}
                formatCreateLabel={(inputValue) => (
                  <div
                    className={clsx([
                      "flex",
                      "gap-2",
                      "w-full",
                      "flex-row",
                      "flex-shrink",
                      "truncate",
                      "cursor-pointer",
                    ])}
                  >
                    <div
                      className={clsx([
                        "inline-flex",
                        "items-center",
                        "justify-center",
                      ])}
                    >
                      <MdAdd size={ICON_SIZES.XS} className="shrink-0" />
                      <span className="font-medium text-sm">
                        {t("select_component.add_option")}
                      </span>
                    </div>
                    <Badge
                      text={inputValue.trim()}
                      className="dark:!bg-cyan-400 dark:!text-black"
                    />
                  </div>
                )}
              />
            ) : (
              <Select
                {...commonProps}
                noOptionsMessage={() => t("select_component.no_elements")}
              />
            )}
          </div>
          {allowSearchModal && (
            <Button
              onClick={() => setShowModalSelect(true)}
              icon={<MdOutlineManageSearch size={ICON_SIZES.MD} />}
              tooltip={t("select_component.choose")}
            />
          )}
        </div>
      </div>
      {showModalSelect && (
        <SelectModal
          onClose={() => setShowModalSelect(false)}
          options={options}
          isMulti={isMulti}
          value={
            isMulti ? ((selectedValue || []) as any[]) : (selectedValue as any)
          }
          onSelect={(value: any) => {
            updateValue(isMulti ? value : { value });
            setShowModalSelect(false);
          }}
        />
      )}
    </>
  );
};

export default CustomSelect;
